"use client";

import SkeletonImage from "@/components/ui/SkeletonImage";
import ProfileLogoBadge, { LOGO_REVEAL_CLASS } from "@/components/ui/ProfileLogoBadge";
import Link from "@/components/ui/LocaleLink";
import { useTranslations } from "next-intl";
import { MapPin, Globe } from "@phosphor-icons/react/dist/ssr";
import VerifiedIcon from "@/components/ui/VerifiedIcon";
import FounderIcon from "@/components/ui/FounderIcon";
import type { Professional } from "@/components/ui/ProfessionalCard";
import { showsVerifiedBadge } from "@/lib/listing-visibility";
import { toLangTokens } from "@/lib/lang-utils";
import { BADGE_TAB_CATEGORY } from "@/lib/badge-styles";
import { CARD_LANG_CODES_SHOWN, LIST_CARD, LIST_CARD_LINK, LIST_CARD_TAB, LIST_CARD_WELL, META_CASE, PHOTO_HOVER_ZOOM } from "@/lib/card-styles";
import { getCategoryLabel, type DirectoryCategory } from "@/lib/categories";
import { formatLocationShort } from "@/lib/location-short";
import { useLocale } from "@/lib/hooks/use-locale";
import type { Locale } from "@/lib/locale";

/**
 * The homepage Featured Professionals card BELOW `sm` (owner, 2026-09-18):
 * the search page's horizontal list card, carrying only what the portrait
 * `ProfessionalCard` carries (photo, category tab, name with badges, title,
 * location, languages). Tablet and desktop keep the portrait grid; the caller
 * shows one or the other by breakpoint. Approved from the phone frame of
 * `design/featured-horizontal-mockup.html` (104px photo, text centred).
 *
 * The frame, the photo well and the tab come from the list card recipes, so
 * the two horizontal cards cannot drift apart. The body is its own: no price,
 * rating, availability or description, and the text is centred beside the
 * photo rather than top-aligned.
 */
export default function FeaturedProMobileCard({ pro, locale: initialLocale = "en" }: { pro: Professional; locale?: Locale }) {
  const locale = useLocale(initialLocale);
  const t = useTranslations("a11y");
  const displayName = pro.displayName ?? pro.name;
  const extra = (pro.provinceSlugs ?? []).filter((s) => s && s !== pro.location).length;
  const tokens = pro.languages ? toLangTokens(pro.languageCodes ?? pro.languages, locale) : [];
  const languagesLabel = tokens.map((tk) => tk.label).join(", ");

  // The badges ride with the LAST word, in one no-wrap pair, so a name that
  // wraps (they do, at 333px: most business names take two lines) keeps them
  // beside its end instead of dropping one onto a line by itself.
  const cut = displayName.lastIndexOf(" ");
  const head = cut < 0 ? "" : displayName.slice(0, cut + 1);
  const last = cut < 0 ? displayName : displayName.slice(cut + 1);

  return (
    <article aria-label={`${displayName}, ${pro.title}`} className={LIST_CARD}>
      <Link href={`/professionals/${pro.slug}`} className={LIST_CARD_LINK}>
        {/* Outer box positions the tab, inner box clips (see LIST_CARD_WELL). */}
        <div className={`${LIST_CARD_WELL} self-center`}>
          <div className="relative aspect-[3/4] overflow-hidden rounded-control">
            {pro.image && pro.imageType === "logo" ? (
              <ProfileLogoBadge
                src={pro.image}
                alt={`${displayName} logo`}
                sizes="104px"
                className="h-full w-full"
                imageClassName={LOGO_REVEAL_CLASS}
              />
            ) : pro.image ? (
              <SkeletonImage
                src={pro.image}
                alt={`${displayName}, ${pro.title}`}
                fill
                sizes="104px"
                className={`object-cover ${PHOTO_HOVER_ZOOM}`}
              />
            ) : (
              <div className="h-full w-full bg-navy/5" aria-hidden="true" />
            )}
          </div>
          <span className={`${BADGE_TAB_CATEGORY} ${LIST_CARD_TAB}`}>
            {getCategoryLabel(pro.category as DirectoryCategory, locale) ?? pro.category}
          </span>
        </div>

        <div className="min-w-0 self-center py-13 pl-8 pr-13">
          {/* No dot between the items: the icons separate them, and a wrapped
              line would otherwise start or end on a stray dot. */}
          <p className={`flex flex-wrap items-center gap-x-13 gap-y-2 text-3xs text-gray-500 ${META_CASE}`}>
            <span className="flex shrink-0 items-center gap-3 whitespace-nowrap">
              <MapPin aria-hidden="true" weight="bold" className="h-13 w-13 shrink-0" />
              {formatLocationShort(pro.location, locale)}
              {extra > 0 && (
                <>
                  <span aria-hidden="true">· +{extra}</span>
                  <span className="sr-only">{t("plusNLocations", { n: extra })}</span>
                </>
              )}
            </span>
            {tokens.length > 0 && (
              <span className="flex shrink-0 items-center gap-3 whitespace-nowrap" aria-label={languagesLabel} title={languagesLabel}>
                <Globe aria-hidden="true" weight="bold" className="h-13 w-13 shrink-0" />
                <span aria-hidden="true">
                  {tokens.slice(0, CARD_LANG_CODES_SHOWN).map((tk) => tk.code).join(" / ")}
                  {tokens.length > CARD_LANG_CODES_SHOWN && ` +${tokens.length - CARD_LANG_CODES_SHOWN}`}
                </span>
              </span>
            )}
          </p>

          {/* The name WRAPS as far as it needs to. It is deliberately NOT
              line-clamped: the mockup clamped it to two lines to keep three
              desktop columns the same height, and in a one-column phone list
              that buys nothing while silently eating a third line. Measured at
              375px: a 42-character company name overflowed the clamp AND took
              the verified badge with it, since the badge rides with the last
              word. Today's longest listing is 22 characters, so this is a
              guard for the names the directory will grow, not a live fix. */}
          <h3 className="mt-8 font-serif text-base font-semibold leading-tight text-navy">
            {head}
            <span className="whitespace-nowrap">
              {last}
              {(pro.founder || showsVerifiedBadge(pro)) && <span className="ml-5 inline-flex items-center gap-5 align-middle">
                {pro.founder && <FounderIcon size={15} nudge={0} />}
                {showsVerifiedBadge(pro) && <VerifiedIcon size={15} nudge={0} />}
              </span>}
            </span>
          </h3>
          <p className="mt-3 text-xs leading-normal text-gray-500">{pro.title}</p>
        </div>
      </Link>
    </article>
  );
}
